import { Box, Button, Typography } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { useFormContext } from "react-hook-form"

import { FormAutocomplete, FormDatePicker, FormMultiSelect } from "@/components/UiKit"
import type { PriceChangeFilterOptions } from "@/graphql/codegen/graphql"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import { priceChangeTypeLabelMap } from "@/screens/Pricing/constants/priceChangeLabelMap"
import type { PriceChangeFilterFormValues } from "@/screens/Pricing/schema/PriceChangeFilterFormSchema"
import { mapPriceChangeLookupItemsToOptions } from "@/screens/Pricing/utils/lookup"

const changeTypeOptions = Object.values(PriceChangeType).map((value) => ({
  value: value.toString(),
  label: priceChangeTypeLabelMap.get(value) ?? value,
}))

interface PriceChangeFilterFormProps {
  onSubmit: (data: PriceChangeFilterFormValues) => Promise<void>
  onClearAll: () => void
  hasFilterParams: boolean
  canApplyFilters?: boolean
  filterOptions?: PriceChangeFilterOptions | null
}

const PriceChangeFilterForm: FC<PriceChangeFilterFormProps> = ({
  onClearAll,
  onSubmit,
  hasFilterParams,
  canApplyFilters,
  filterOptions,
}) => {
  const {
    control,
    reset,
    watch,
    getValues,
    setValue,
    formState: { isValid, isDirty },
    handleSubmit,
  } = useFormContext<PriceChangeFilterFormValues>()

  const changeTypeFieldValue = watch("changeType")

  const isFilterInputDisabled = !changeTypeFieldValue

  const handleChangeTypeChange = (value: string | null) => {
    reset({
      customerIds: [],
      specificProductIds: [],
      supplierId: null,
      productCategoryId: null,
      productClassId: null,
      productManagerId: null,
      shipFromId: null,
      shipToId: null,
      effectiveDate: null,
    })

    setValue("changeType", value as PriceChangeType | null, {
      shouldValidate: true,
    })
  }

  const handleProductManagerChange = (value: string | null) => {
    if (!value) {
      reset({
        ...getValues(),
        customerIds: [],
        specificProductIds: [],
        supplierId: null,
        productCategoryId: null,
        productClassId: null,
        shipFromId: null,
        shipToId: null,
        effectiveDate: null,
      })
    }

    setValue("productManagerId", value, {
      shouldValidate: true,
    })
  }

  return (
    <form>
      <Box className='flex flex-row justify-between gap-3'>
        <Box className='flex w-4/5 flex-row justify-start gap-3'>
          <FormAutocomplete
            control={control}
            name='changeType'
            label='Change Type'
            placeholder='Select Change Type'
            onChange={handleChangeTypeChange}
            options={changeTypeOptions}
            required
            data-testid='pricing.change-form.change-type-field'
          />

          <FormAutocomplete
            control={control}
            name='supplierId'
            label='Supplier'
            placeholder='Select Supplier'
            disabled={isFilterInputDisabled}
            options={mapPriceChangeLookupItemsToOptions(filterOptions?.suppliers)}
            data-testid='pricing.change-form.supplier-field'
          />

          {changeTypeFieldValue === PriceChangeType.SellingPrice && (
            <FormMultiSelect
              control={control}
              name='customerIds'
              label='Customer'
              placeholder='Select Customer'
              disabled={isFilterInputDisabled}
              options={mapPriceChangeLookupItemsToOptions(filterOptions?.customers)}
              data-testid='pricing.change-form.customer-field'
            />
          )}

          <FormAutocomplete
            control={control}
            name='productCategoryId'
            label='Product Category'
            placeholder='Select Product Category'
            disabled={isFilterInputDisabled}
            options={mapPriceChangeLookupItemsToOptions(filterOptions?.productCategories)}
            data-testid='pricing.change-form.product-category-field'
          />
        </Box>
        <Box className='flex w-1/5 justify-end'>
          <FormAutocomplete
            control={control}
            name='productClassId'
            label='Product Class'
            placeholder='Select Product Class'
            disabled={isFilterInputDisabled}
            options={mapPriceChangeLookupItemsToOptions(filterOptions?.productClasses)}
            data-testid='pricing.change-form.product-class-field'
          />
        </Box>
      </Box>

      <Box className='mt-6 flex justify-between gap-3'>
        <Box className='flex w-4/5 gap-3'>
          {changeTypeFieldValue === PriceChangeType.SellingPrice && (
            <FormAutocomplete
              control={control}
              name='productManagerId'
              label='Product Manager'
              required
              onChange={handleProductManagerChange}
              disabled={!changeTypeFieldValue}
              placeholder='Select Product Manager'
              options={mapPriceChangeLookupItemsToOptions(filterOptions?.productManagers)}
              data-testid='pricing.change-form.product-manager-field'
            />
          )}
          <FormMultiSelect
            control={control}
            name='specificProductIds'
            label='Specific Product(s)'
            placeholder='Select Specific Product(s)'
            disabled={isFilterInputDisabled}
            options={mapPriceChangeLookupItemsToOptions(filterOptions?.products)}
            data-testid='pricing.change-form.specific-products-field'
          />

          <FormAutocomplete
            control={control}
            name='shipFromId'
            label='Ship From'
            placeholder='Select Ship From'
            disabled={isFilterInputDisabled}
            options={mapPriceChangeLookupItemsToOptions(filterOptions?.shipFromWarehouseAreas)}
            data-testid='pricing.change-form.ship-from-field'
          />

          {changeTypeFieldValue === PriceChangeType.PurchasingCost && (
            <FormAutocomplete
              control={control}
              name='shipToId'
              label='Ship To'
              placeholder='Select Ship To'
              disabled={isFilterInputDisabled || !filterOptions?.shipToLocations?.length}
              options={mapPriceChangeLookupItemsToOptions(filterOptions?.shipToLocations)}
              data-testid='pricing.change-form.ship-to-field'
            />
          )}

          <FormDatePicker
            control={control}
            name='effectiveDate'
            label='Effective Date'
            disabled={isFilterInputDisabled}
            data-testid='pricing.change-form.effective-date-field'
          />
        </Box>

        <Box className='mt-6 flex w-1/5 gap-3 self-start whitespace-nowrap'>
          <Button
            className='bg-white capitalize'
            type='submit'
            variant='outlined'
            disabled={!canApplyFilters || !isValid || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filters
          </Button>

          <Button className='capitalize' disabled={!hasFilterParams} onClick={onClearAll}>
            Clear all
          </Button>
        </Box>
      </Box>

      {Boolean(changeTypeFieldValue) && changeTypeFieldValue !== PriceChangeType.SellingPrice && !canApplyFilters && (
        <Box className='mt-6 flex flex-row justify-end'>
          <Typography className='text-sm text-gray-700'>
            Select at least 2 filters (including Change Type) to apply.
          </Typography>
        </Box>
      )}
    </form>
  )
}

export default PriceChangeFilterForm
